/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 * Main component Table
 *
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Maxim Fedorov
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.0.0
 */

import type { FC, RefObject } from 'react';
import { cloneElement, Fragment, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import debounce from 'lodash/debounce';
import delay from 'lodash/delay';
import moment from 'moment';
import ReactDOM from 'react-dom';
import { Row } from 'react-table';

import { DEBOUNCE_DEFAULT_DELAY } from '@modules/app/appConstants';
import type { IFixedColumns } from '@modules/app/hoc/WithFixedColumns/helpers';
import { enableStickyPosition } from '@modules/app/hoc/WithFixedColumns/helpers';
import WithFixedColumns from '@modules/app/hoc/WithFixedColumns/WithFixedColumns';
import { ONBOARDING_DATA_ATTR } from '@modules/onboarding/onboardingConstants';
import { EOnboardingComponentsId } from '@modules/onboarding/types/onboardingTypes';
import { EFormatData } from '@pages/PageDashboard/types/widgetTypes';
import { table } from '@theme/index';

import CheckedColumn from './components/TableColumns/CheckedColumn';
import ExpandedColumn from './components/TableColumns/ExpandedColumn';
import TableTooltip from './components/TableTooltip/TableTooltip';
import { CHECKED_COLUMN_NAME, TABLE_FOOTER_BOTTOM_STYLE } from './constants';
import type { ITableField, ITableReducer } from './reducers/tableReducer';
import {
  HorizontalScrollBlockContainerStyled,
  HorizontalScrollBlockStyled,
  HorizontalScrollStyled,
  TableContainerStyled,
  TableFooterNavStyled,
  TableFooterStyled,
} from './styled/TableStyled';
import './Table.css';
import TableNoData from './TableNoData';
import type {
  IFieldResize,
  ISetTableSortPayload,
  ISwitchCheckboxPayload,
  ITableFooterSize,
} from './types/tableTypes';
import { TableNameType } from './types/tableTypes';
import { getTooltipPosition } from './utils/tableUtils';

export interface ITableProps {
  basketAllIn?: boolean;
  columns: IFixedColumns;
  counter: number;
  data: ITableReducer[TableNameType]['data'];
  disableCheckedAll?: boolean;
  disableCheckedAllMessage?: string;
  disableSortCheckbox?: boolean;
  fields?: Array<ITableField<any>>;
  footerHeight?: number;
  forceFooterUpdate?: boolean;
  innerRef?: RefObject<HTMLDivElement>;
  isLoading: boolean;
  isTableFooter?: boolean;
  loadTableList?: () => void;
  onboarding?: EOnboardingComponentsId;
  onChangeFooterSize?: (footerSize: ITableFooterSize) => void;
  onCollapseRow?: (rowId: number) => void;
  onExpandRow?: (rowId: number) => void;
  onScrollEnd?: () => void;
  onScrollX?: (scrollLeft: number) => void;
  removedRowIndexes?: ITableReducer[TableNameType]['removedRowIndexes'];
  rowDataName?: string;
  saveResizedChange?: (fields: IFieldResize[]) => void;
  scrollbarBottom?: string;
  setTableSort: (payload: ISetTableSortPayload) => void;
  showCheckedColumn?: boolean;
  sideBarWidth?: number;
  sort: ITableReducer[TableNameType]['sort'];
  switchCheckbox?: (payload: ISwitchCheckboxPayload) => void;
  tableDataName?: string;

  [key: string]: any;
}

const enableSticky: boolean = enableStickyPosition();
let tableBodyHorizontalScrolled = false;

export const Table: FC<ITableProps> = (props) => {
  const [isFooterVisible, setFooterVisible] = useState(!!props.footer);
  const [tableHeadNode, setTableHeadNode] = useState<HTMLElement | null>(null);
  const [tableBodyNode, setTableBodyNode] = useState<HTMLElement | null>(null);
  const [tableNode, setTableNode] = useState<HTMLElement | null>(null);
  const [tableFooterNode, setTableFooterNode] =
    useState<HTMLElement | null>(null);
  const [tableHorScrollWidth, setTableHorScrollWidth] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);
  const [showHorizontalScroll, setShowHorizontalScroll] = useState(false);
  const [isTableWasLoaded, setTableWasLoaded] = useState(false);

  const tableContainerRef: RefObject<HTMLDivElement> =
    useRef<HTMLDivElement>(null);
  const tableHorScrollContainerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<any>(null);
  const tableTooltipRef: RefObject<HTMLDivElement> =
    useRef<HTMLDivElement>(null);
  const tableTooltipTextRef: RefObject<HTMLSpanElement> =
    useRef<HTMLSpanElement>(null);
  const tableTooltipMessageRef: RefObject<HTMLSpanElement> =
    useRef<HTMLSpanElement>(null);
  const tableFooterNavRef = useRef<HTMLDivElement>(null);

  const isNoDataShown = props.data && props.data.length === 0 && !props.isLoading && !props.counter;
  const rowPadding = props.isDensity
    ? table.densityRowPadding
    : table.defaultRowPadding;
  const rowHeight = {
    default: table.defaultRowHeight + 2 * rowPadding,
    defaultDouble: table.doubleRowHeight + 2 * rowPadding,
    next: table.nextRowHeight,
  };

  let tBodyBlock: HTMLDivElement | null;
  let tooltipBlock: HTMLDivElement | null;
  let startTouchMove = false;
  let touchMovePoint = 0;
  const setScrollWidth = debounce(() => {
    setTableHorScrollWidth(
      props.data.length === 0 || !props.data
        ? tableHeadNode?.scrollWidth ?? 0
        : tableBodyNode?.scrollWidth ?? 0,
    );
    setFooterHeight(getFooterSize().footerHeight);
  }, 300);

  const saveResizedChangeDebounce = debounce((fields) => {
    if (props.saveResizedChange) {
      props.saveResizedChange(fields);
    }
  }, DEBOUNCE_DEFAULT_DELAY);

  useEffect(() => {
    findTableNode();
    // Add event listener on table body after DOM render
    setTimeout(() => {
      tBodyBlock =
        // eslint-disable-next-line react-hooks/exhaustive-deps
        tableContainerRef.current?.querySelector('.rt-tbody') ?? null;
      // add event listener on mouse over an elements inside tbody
      tBodyBlock?.addEventListener('mouseover', mouseOverhandler, true);
      // add event listener on mouse leave an elements inside tbody
      tBodyBlock?.addEventListener('mouseleave', mouseLeaveHandler, true);
      // add event listener on mobile screen move
      tBodyBlock?.addEventListener('touchstart', touchStartHandler, true);
      tBodyBlock?.addEventListener('touchmove', touchMoveHandler, true);
      tBodyBlock?.addEventListener('touchend', touchEndHandler, true);
    });
    window.addEventListener('scroll', correctFooterLocation, true);
    setShowHorizontalScroll(
      window.innerHeight - tableRef.current?.getBoundingClientRect().top > 140,
    );
    return () => {
      window.removeEventListener('scroll', correctFooterLocation, true);
      // Remove event listener on table body
      tBodyBlock?.removeEventListener('mouseover', mouseOverhandler, true);
      tBodyBlock?.removeEventListener('mouseleave', mouseLeaveHandler, true);
      tBodyBlock?.removeEventListener('touchstart', touchStartHandler, true);
      tBodyBlock?.removeEventListener('touchmove', touchMoveHandler, true);
      tBodyBlock?.removeEventListener('touchend', touchEndHandler, true);
    };
  }, []);

  useEffect(() => {
    setScrollWidth();
    correctFooterLocation();
  }, [props.data, setScrollWidth]);

  useEffect(() => {
    const footerSize = getFooterSize();
    setFooterHeight(footerSize.footerHeight);
    if (props.onChangeFooterSize) props.onChangeFooterSize(footerSize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFooterVisible, showHorizontalScroll]);

  useEffect(() => {
    setTimeout(() => {
      correctFooterLocation();
    });
  }, [props.forceFooterUpdate]);

  useEffect(() => {
    if (props.isLoading && !isTableWasLoaded) {
      setTableWasLoaded(true);
    }
  }, [props.isLoading, isTableWasLoaded]);

  /**
   * @description Method handles mouse over an elements inside tbody
   * if element is has ellipsis text and "table__tooltip-text" class,
   * start to show a tooltip after 300 ms, if  current tooltipBlock to equal element
   * @param {React.MouseEvent} event mouse event
   */
  const mouseOverhandler = (event: Event): void => {
    const target: HTMLDivElement | null = (
      event?.target as HTMLDivElement
    )?.closest('.table__tooltip-text');
    const tooltip = tableTooltipRef.current;
    const tooltipText = tableTooltipTextRef.current;
    const tooltipMessage = tableTooltipMessageRef.current;
    /**
     * CLM-437032
     * On the subscribers and clients lists there is a tooltip with the full name of the personal status name
     * if the value does not fit.
     * It is necessary to supplement such hint with an explanation after the full name of the status,
     * if the backend returned the persDataHintCode code.
     * If the backend did not return persDataHintCode and the value is placed the tooltip should be absent.
     */
    if (target && target.classList.contains('table__personalStatus-text')) {
      tooltipBlock = target;
      delay(() => {
        if (tooltip && tooltipText && tooltipBlock && tooltipBlock === target) {
          const hint = tooltipBlock.querySelector('.personal-data-hint');
          const span = tooltipBlock.querySelector('span');
          if (hint || target.offsetWidth < target.scrollWidth) {
            tooltipText.innerText = span?.innerText || tooltipBlock.innerText;
            if (tooltipMessage) {
              tooltipMessage.innerHTML = hint?.innerHTML || '';
            }
            const blockStyle = tooltipBlock.getBoundingClientRect();
            const styleTooltip = tooltip.getBoundingClientRect();
            const { left, top } = getTooltipPosition(blockStyle, styleTooltip);
            tooltip.style.transform = `translate(${left}px, ${top}px)`;
            tooltip.classList.add('visible');
          }
        }
      }, 300);
    } else if (target && target.offsetWidth < target.scrollWidth) {
      tooltipBlock = target;
      delay(() => {
        if (tooltip && tooltipText && tooltipBlock && tooltipBlock === target) {
          const span = tooltipBlock.querySelector('span');
          if (span) {
            tooltipText.innerText = span.innerText;
          } else {
            tooltipText.innerText = tooltipBlock.innerText;
          }
          const blockStyle = tooltipBlock.getBoundingClientRect();
          const styleTooltip = tooltip.getBoundingClientRect();
          const { left, top } = getTooltipPosition(blockStyle, styleTooltip);
          tooltip.style.transform = `translate(${left}px, ${top}px)`;
          tooltip.classList.add('visible');
        }
      }, 300);
    }
  };
  /**
   * @description Method handles mouse leave an elements inside tbody
   * method hide tooltip, and clear tooltipBlock element
   */
  const mouseLeaveHandler = (event: MouseEvent): void => {
    const tooltip = tableTooltipRef.current;
    if (
      tooltipBlock &&
      (event.target as HTMLDivElement)?.classList.contains('rt-td')
    ) {
      if (tooltip && tooltipBlock) {
        tooltipBlock = null;
        tooltip.classList.remove('visible');
      }
    }
  };

  const correctFooterLocation = (): void => {
    const tableY = tableContainerRef.current?.getBoundingClientRect().top ?? 0;
    setShowHorizontalScroll(window.innerHeight - tableY > 140);
  };
  const touchStartHandler = (event: TouchEvent): void => {
    if ((event?.target as Element).closest('.rt-table')) {
      startTouchMove = true;
      touchMovePoint = event.touches[0].pageX;
    }
  };
  const touchMoveHandler = (event: TouchEvent): void => {
    if (startTouchMove) {
      const delta = touchMovePoint - event.touches[0].pageX;
      tableHorScrollContainerRef.current?.scrollBy(delta, 0);
      touchMovePoint = event.touches[0].pageX;
    }
  };
  const touchEndHandler = (): void => {
    startTouchMove = false;
    touchMovePoint = 0;
  };

  const setScrolledClass = (scrolled: boolean): void => {
    if (tableBodyHorizontalScrolled !== scrolled) {
      tableBodyHorizontalScrolled = scrolled;
      if (scrolled) {
        tableContainerRef.current?.classList?.add('scrolled');
      } else {
        tableContainerRef.current?.classList?.remove('scrolled');
      }
    }
  };
  /**
   * @description The method handles scroll events
   */
  const horizontalScrollHandler = (): void => {
    const scroll = tableHorScrollContainerRef.current;
    if (tableBodyNode && tableHeadNode && scroll) {
      const scrolled = scroll.scrollLeft > 0;
      setScrolledClass(scrolled);
      if (tableBodyNode.scrollTo && tableHeadNode.scrollTo) {
        tableBodyNode.scrollTo({
          left: scroll.scrollLeft,
        });
        tableHeadNode.scrollTo({
          left: scroll.scrollLeft,
        });
        tableFooterNode?.scrollTo({
          left: scroll.scrollLeft,
        });
      } else {
        if (tableNode) {
          tableNode.scrollLeft = scroll.scrollLeft;
        } else {
          tableBodyNode.scrollLeft = scroll.scrollLeft;
          tableHeadNode.scrollLeft = scroll.scrollLeft;
          if (tableFooterNode && !tableFooterNode.scrollTo) {
            tableFooterNode.scrollLeft = scroll.scrollLeft;
          }
        }
      }
    }
  };
  /**
   * @description The method handles tabIndex scroll events
   */
  const tabScrollHandler = (event: Event): void => {
    const scrollLeft = (event?.target as HTMLDivElement)?.scrollLeft;
    const scroll = tableHorScrollContainerRef.current;
    if (
      (scrollLeft || scrollLeft === 0) &&
      scroll &&
      scrollLeft !== scroll.scrollLeft &&
      props.data.length
    ) {
      scroll.scrollLeft = scrollLeft;
      horizontalScrollHandler();
    }
  };

  const checkedRow = (checked: boolean, row: Row<any>): void => {
    if (!props.switchCheckbox) return;

    if (row) {
      let items: any[];
      if (props.tableName === TableNameType.bills) {
        items = [
          {
            id: row.original.id,
            label: row.original.billNumber,
            params: {
              billPeriod: moment(row.original.startDate ?? '').format(
                EFormatData.YearMonthReverse,
              ),
              clientId: row.original.clientId,
              number: row.original.customer,
              startDate: moment(row.original.startDate ?? '').format(
                EFormatData.FullData,
              ),
            },
            value: checked,
          },
        ];
      } else if (props.tableName === TableNameType.expensesAnalyzer) {
        items = [
          {
            id: row.original.subscriberId || row.original.bisAccountId,
            label: row.original.subscriberId
              ? row.original.msisdn
              : row.original.account,
            value: checked,
          },
        ];
      } else {
        items = [
          {
            id: row.original.id,
            label: row.original?.msisdn || (row.original?.number ?? ''),
            value: checked,
          },
        ];
      }
      props.switchCheckbox({ items });
    } else {
      props.switchCheckbox({
        isAllChecked: checked,
      });
    }
  };

  const getFooterSize = (): ITableFooterSize => {
    const tableFooterNavTop =
      tableFooterNavRef?.current?.getBoundingClientRect().top ?? 0;
    const tableHorScrollTop =
      tableHorScrollContainerRef?.current?.getBoundingClientRect().top ??
      tableFooterNavTop;

    const top = tableHorScrollTop
      ? window.innerHeight - tableHorScrollTop
      : TABLE_FOOTER_BOTTOM_STYLE;
    const outerScroll =
      window.innerHeight - document.documentElement.clientHeight;

    return {
      footerHeight: top - outerScroll - 2,
      horScrollHeight: tableFooterNavTop - tableHorScrollTop,
    };
  };

  // Add our expander column
  if (props.onExpandRow && props.onCollapseRow) {
    props.columns.unshift(
      ExpandedColumn({
        onCollapseRow: props.onCollapseRow,
        onExpandRow: props.onExpandRow,
        collapseTableSubRows: props.collapseTableSubRows,
      }),
    );
  }

  if (props.showCheckedColumn) {
    if (props.columns[0]?.accessor !== CHECKED_COLUMN_NAME) {
      props.columns.unshift(
        CheckedColumn({
          checkedAll: props.basketAllIn ?? false,
          disableCheckedAll: props.disableCheckedAll,
          disableCheckedAllMessage: props.disableCheckedAllMessage,
          onChange: checkedRow,
          onSort: props.setTableSort,
          sort: props.sort,
          unsortable: props.disableSortCheckbox,
        }),
      );
    }
  }

  /**
   * @description Footer icon handler for open / close footer container
   * @return{VoidFunction}
   */
  const onFooterClick = (): void => {
    setFooterVisible(!isFooterVisible);
  };

  /**
   * @description Find table body and head nodes
   * @return{VoidFunction}
   */
  const findTableNode = (): void => {
    /**
     * If browser supports "position: sticky" create outside scrollbars
     */
    //eslint-disable-next-line react/no-find-dom-node
    const findDOMNode = ReactDOM.findDOMNode(tableRef.current) as ParentNode;
    if (findDOMNode) {
      const head = findDOMNode.querySelector<HTMLElement>('.rt-thead');
      const body = findDOMNode.querySelector<HTMLElement>('.rt-tbody');
      const footer = findDOMNode.querySelector<HTMLElement>('.rt-tfooter');
      setTableHeadNode(head);
      setTableBodyNode(body);
      setTableFooterNode(footer);
      head?.addEventListener('scroll', tabScrollHandler, true);
      body?.addEventListener('scroll', tabScrollHandler, true);
      footer?.addEventListener('scroll', tabScrollHandler, true);
      if (!enableSticky) {
        const table = findDOMNode.querySelector<HTMLElement>('.rt-table');
        setTableNode(table);
      }
    }
  };

  const onResizeChange = (newResized: IFieldResize[]): void => {
    const newFields = newResized.map((item: IFieldResize) => ({
      id: item.id,
      value: item.value,
    }));
    saveResizedChangeDebounce(newFields);
    setScrollWidth();
  };

  const renderFooter = () =>
    props.footer &&
    cloneElement(props.footer, {
      isOpen: isFooterVisible,
      onClick: onFooterClick,
      switchCheckbox: props.switchCheckbox,
      tableCheckedItems: props.tableCheckedItems,
    });

  const setHorScrollBlockWidth = tableNode
    ? `${tableNode.offsetWidth}px`
    : tableHeadNode
    ? `${tableHeadNode.offsetWidth}px`
    : 'auto';

  /*
   * -------------------------------------------
   *         TABLE CLASSNAME
   * -------------------------------------------
   */
  const tableContainerClassname: string = classNames('table-container', {
    'table-container_loading': props.isLoading,
    'table-no-data': props.data && props.data.length === 0 && !props.isLoading,
  });

  /**
   * --------------------------------
   *      If sticky doesn't support
   * --------------------------------
   */

  const tableMenuRef = useRef<HTMLDivElement>(null);

  const onTableScrollX = (scrollLeft: number) => {
    setScrolledClass(scrollLeft > 0);
  };

  const showFooter = (): boolean => {
    return (
      !(props.data && props.data.length === 0 && !props.isLoading) &&
      (props.isTableFooter ?? false)
    );
  };

  return (
    <TableContainerStyled
      className={tableContainerClassname}
      footerHeight={footerHeight}
      ref={tableContainerRef}
      rowPadding={rowPadding}
    >
      {!enableSticky && (
        <Fragment>
          <WithFixedColumns
            {...props}
            className={props.className}
            filterable={false}
            footerHeight={footerHeight}
            innerRef={tableRef}
            isTableFooter={showFooter()}
            minRows={0}
            pageSize={props.data.length}
            rowHeight={rowHeight}
            showPagination={false}
            sortable={false}
            style={{
              height: `calc(100vh - ${footerHeight}px)`,
              marginBottom: `${footerHeight}px`,
            }}
            onResizedChange={onResizeChange}
            onScrollEnd={props.loadTableList}
            onScrollX={onTableScrollX}
          />
          <TableFooterStyled ref={tableMenuRef}>
            {showHorizontalScroll && (
              <HorizontalScrollBlockContainerStyled>
                <HorizontalScrollBlockStyled
                  bottomPosition={props.scrollbarBottom}
                  marginLeft={props.sideBarWidth}
                  ref={tableHorScrollContainerRef}
                  width={setHorScrollBlockWidth}
                  onScroll={horizontalScrollHandler}
                >
                  <HorizontalScrollStyled
                    style={{ width: tableHorScrollWidth }}
                  />
                </HorizontalScrollBlockStyled>
              </HorizontalScrollBlockContainerStyled>
            )}
            <TableFooterNavStyled ref={tableFooterNavRef}>
              {renderFooter()}
            </TableFooterNavStyled>
          </TableFooterStyled>
        </Fragment>
      )}
      {enableSticky && (
        <Fragment>
          <div {...{ [ONBOARDING_DATA_ATTR]: props.onboarding }}>
            <WithFixedColumns
              {...props}
              className={isFooterVisible ? 'footer-open' : 'footer-close'}
              filterable={false}
              footerHeight={footerHeight}
              innerRef={tableRef}
              isTableFooter={showFooter()}
              minRows={0}
              pageSize={props.data.length}
              rowHeight={rowHeight}
              showPagination={false}
              sortable={false}
              onResizedChange={onResizeChange}
              onScrollEnd={props.loadTableList}
            />
          </div>
          <TableFooterStyled>
            {showHorizontalScroll && (
              <HorizontalScrollBlockContainerStyled>
                <HorizontalScrollBlockStyled
                  bottomPosition={props.scrollbarBottom}
                  marginLeft={props.sideBarWidth}
                  ref={tableHorScrollContainerRef}
                  width={setHorScrollBlockWidth}
                  onScroll={horizontalScrollHandler}
                >
                  <HorizontalScrollStyled
                    style={{ width: tableHorScrollWidth }}
                  />
                </HorizontalScrollBlockStyled>
              </HorizontalScrollBlockContainerStyled>
            )}
            <TableFooterNavStyled ref={tableFooterNavRef}>
              {renderFooter()}
            </TableFooterNavStyled>
          </TableFooterStyled>
        </Fragment>
      )}
      {isNoDataShown && (
        <TableNoData isDataLoaded={isTableWasLoaded} />
      )}
      <TableTooltip
        tooltipMessageRef={tableTooltipMessageRef}
        tooltipRef={tableTooltipRef}
        tooltipTextRef={tableTooltipTextRef}
      />
    </TableContainerStyled>
  );
};

export default Table;
